import React, { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useNavigate } from "react-router-dom";

import { postClaimMyClinicInvite } from "../../services/api";
import global from "../../styles/globalStyles";
import { supabase } from "../../utils/supabase";
import { titles, states } from "../../utils/constant";

import {
  Header,
  Input,
  FooterLinks,
  PrimaryButton,
  SecondaryButton,
  InputDropdown,
} from "../common/native/index";

const SetupPassword = () => {
  const navigate = useNavigate();
  const initialStepOverride = Number(
    (globalThis as any).__TEST_SETUP_PASSWORD_INITIAL_STEP__ ?? 1,
  );
  const initialStep: 1 | 2 | 3 =
    initialStepOverride === 2 || initialStepOverride === 3
      ? initialStepOverride
      : 1;
  const [step, setStep] = useState<1 | 2 | 3>(initialStep);

  const [loading, setLoading] = useState<boolean>(false);

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [title, setTitle] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");

  const [jobTitle, setJobTitle] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [state, setState] = useState<string>("");

  const [errorMessageStep1, setErrorMessageStep1] = useState<string>("");
  const [errorMessageStep2, setErrorMessageStep2] = useState<string>("");
  const [errorMessageStep3, setErrorMessageStep3] = useState<string>("");

  React.useEffect(() => {
    const prefill = (globalThis as any).__TEST_SETUP_PASSWORD_PREFILL__;
    if (!prefill) {
      return;
    }
    if (typeof prefill.password === "string") setPassword(prefill.password);
    if (typeof prefill.confirmPassword === "string")
      setConfirmPassword(prefill.confirmPassword);
    if (typeof prefill.title === "string") setTitle(prefill.title);
    if (typeof prefill.fullName === "string") setFullName(prefill.fullName);
    if (typeof prefill.jobTitle === "string") setJobTitle(prefill.jobTitle);
    if (typeof prefill.phoneNumber === "string")
      setPhoneNumber(prefill.phoneNumber);
    if (typeof prefill.state === "string") setState(prefill.state);
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessageStep3("");
    try {
      const updateUserFn =
        (globalThis as any).__TEST_SETUP_PASSWORD_UPDATE_USER__ ??
        supabase.auth.updateUser.bind(supabase.auth);
      const { error } = await updateUserFn({
        password: password,
        data: {
          title: title,
          fullName: fullName,
          jobTitle: jobTitle,
          phoneNumber: phoneNumber,
          state: state,
        },
      });
      if (error) {
        setErrorMessageStep3(error.message);
        return;
      }

      const clinicId = new URLSearchParams(window.location.search)
        .get("clinic_id")
        ?.trim();
      if (!clinicId) {
        setErrorMessageStep3(
          "Invalid invite link. Ask your clinic admin to send a new invite.",
        );
        return;
      }

      const claimInviteFn =
        (globalThis as any).__TEST_POST_CLAIM_MY_CLINIC_INVITE__ ??
        postClaimMyClinicInvite;
      const claimResult = await claimInviteFn(clinicId);
      if (!claimResult?.claimed && !claimResult?.already_member) {
        setErrorMessageStep3(
          "Invite claim did not complete. Ask your clinic admin to send a new invite.",
        );
        return;
      }
      const testNavigate = (globalThis as any).__TEST_SETUP_PASSWORD_NAVIGATE__;
      if (typeof testNavigate === "function") {
        testNavigate("/home");
      } else {
        navigate("/home");
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessageStep3(error.message);
      } else {
        setErrorMessageStep3("Error in Updating Password. Please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Header />
      <View
        style={{
          width: "100%",
          maxWidth: 434,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {step === 1 && (
          <>
            <Text
              style={[
                global.text,
                global.fontSize20,
                global.fontWeight600,
                global.alignSelfStart,
              ]}
            >
              Step 1 of 3
            </Text>
            <Text
              style={[
                global.title,
                global.alignSelfStart,
                { marginBottom: 24 },
              ]}
            >
              Sign up to join your team
            </Text>
            <Input
              name="Password"
              placeholder="Enter your password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              required
            />
            <Input
              name="Confirm Password"
              placeholder="Enter your password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              required
            />
            <View style={{ marginBottom: 12 }} />
            {errorMessageStep1 !== "" && (
              <Text style={global.errorText}>{errorMessageStep1}</Text>
            )}
            <View style={{ marginBottom: 12 }} />
            <View style={{ marginLeft: "auto" }}>
              <PrimaryButton
                text="Next"
                width={140}
                disabled={password && confirmPassword ? false : true}
                onPress={() => {
                  if (password.length < 8) {
                    setErrorMessageStep1("Invalid password");
                    return;
                  }

                  if (password !== confirmPassword) {
                    setErrorMessageStep1(
                      "Please make sure your passwords match.",
                    );
                    return;
                  }
                  setErrorMessageStep1("");
                  setStep(2);
                }}
              />
            </View>
          </>
        )}

        {step === 2 && (
          <>
            <Text
              style={[
                global.text,
                global.fontSize20,
                global.fontWeight600,
                global.alignSelfStart,
              ]}
            >
              Step 2 of 3
            </Text>
            <Text
              style={[
                global.title,
                global.alignSelfStart,
                { marginBottom: 24 },
              ]}
            >
              Tell us about yourself
            </Text>
            <View style={{ zIndex: 1, width: "100%" }}>
              <InputDropdown
                onSelect={setTitle}
                required
                name={"Title"}
                selections={titles}
                value={title}
              />
            </View>
            <Input
              name="Full name"
              placeholder=""
              value={fullName}
              onChangeText={setFullName}
              required
            />
            <View style={{ marginBottom: 12 }} />
            {errorMessageStep2 !== "" && (
              <Text style={global.errorText}>{errorMessageStep2}</Text>
            )}
            <View style={{ marginBottom: 12 }} />
            <View style={styles.backNextBtnsContainer}>
              <SecondaryButton
                text="Back"
                width={140}
                onPress={() => setStep(1)}
              />
              <PrimaryButton
                text="Next"
                width={140}
                onPress={() => setStep(3)}
                disabled={title && fullName ? false : true}
              />
            </View>
          </>
        )}

        {step === 3 && (
          <>
            <Text
              style={[
                global.text,
                global.fontSize20,
                global.fontWeight600,
                global.alignSelfStart,
              ]}
            >
              Step 3 of 3
            </Text>
            <Text
              style={[
                global.title,
                global.alignSelfStart,
                { marginBottom: 32 },
              ]}
            >
              Tell us about your business
            </Text>
            <View style={{ zIndex: 1, width: "100%" }}>
              <InputDropdown
                onSelect={setState}
                required
                allowSearch
                name={"State"}
                selections={states}
                value={state}
              />
            </View>
            <Input
              name="Job Title"
              value={jobTitle}
              onChangeText={setJobTitle}
            />
            <Input
              name="Phone number"
              type="numeric"
              value={phoneNumber}
              onChangeText={setPhoneNumber}
            />
            <View style={{ marginBottom: 12 }} />
            {errorMessageStep3 !== "" && (
              <Text style={global.errorText}>{errorMessageStep3}</Text>
            )}
            <View style={{ marginBottom: 12 }} />
            <View style={styles.backNextBtnsContainer}>
              <SecondaryButton
                text="Back"
                width={140}
                onPress={() => setStep(2)}
              />
              <PrimaryButton
                text="Next"
                width={140}
                onPress={() => handleSubmit()}
                disabled={state ? false : true}
                loading={loading}
              />
            </View>
          </>
        )}
      </View>
      <FooterLinks />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "space-between",
    minHeight: "100%",
    width: "100%",
    gap: 32,
    padding: 16,
  },
  backNextBtnsContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

export default SetupPassword;
